// ---------------------------------------------
// Lightbox Module - VAVE Wear
// ---------------------------------------------
// Highend Lightbox mit Zoom, Pan, Keyboard + Touch
// ---------------------------------------------

import { $ } from '../utils/helpers.js';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.5;
const WHEEL_ZOOM_INTENSITY = 0.0025;
const DOUBLE_TAP_THRESHOLD = 300;
const DOUBLE_TAP_DISTANCE = 30;

export class Lightbox {
    constructor() {
        this.triggers = [];
        this.group = [];
        this.index = -1;
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.minScale = MIN_SCALE;
        this.maxScale = MAX_SCALE;
        this.zoomStep = ZOOM_STEP;
        this.lastTrigger = null;
        this.isOpen = false;

        this.el = null;
        this.stage = null;
        this.wrap = null;
        this.image = null;
        this.zoomLevel = null;
        this.counterCurrent = null;
        this.counterTotal = null;
        this.prevBtn = null;
        this.nextBtn = null;

        this.isDragging = false;
        this.dragStartX = 0;
        this.dragStartY = 0;
        this.dragStartTx = 0;
        this.dragStartTy = 0;
        this.dragMoved = false;

        this.touchState = null;
        this.lastTapTime = 0;
        this.lastTapX = 0;
        this.lastTapY = 0;

        this._handlers = {};
        this._delegatedClick = this._onDocumentClick.bind(this);
        this._delegatedKey = this._onDocumentKeydown.bind(this);

        this._init();
    }

    _init() {
        document.addEventListener('click', this._delegatedClick, false);
        document.addEventListener('keydown', this._delegatedKey, false);
    }

    _onDocumentClick(e) {
        if (e.defaultPrevented) return;
        if (e.button !== undefined && e.button !== 0) return;
        const trigger = e.target.closest('[data-lightbox-trigger]');
        if (trigger) {
            e.preventDefault();
            this.open(trigger);
        }
    }

    _onDocumentKeydown(e) {
        if (!this.isOpen) return;
        if (e.key === 'Escape') {
            e.preventDefault();
            this.close();
        }
    }

    open(trigger) {
        this.lastTrigger = trigger;
        this._collectGroup(trigger);
        this.index = this.group.indexOf(trigger);
        if (this.index < 0) {
            this.group.push(trigger);
            this.index = this.group.length - 1;
        }

        if (!this.el) {
            this._build();
        }
        this._render();
        this._saveScrollbarWidth();
        document.body.classList.add('is-lightbox-open');
        this.el.classList.add('is-open');
        this.isOpen = true;
        this._attachStageEvents();

        if (this.closeBtn) {
            this.closeBtn.focus();
        }
    }

    close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.el.classList.remove('is-open');
        document.body.classList.remove('is-lightbox-open');
        document.documentElement.style.removeProperty('--scrollbar-width');
        this._detachStageEvents();

        if (this.lastTrigger && document.contains(this.lastTrigger)) {
            this.lastTrigger.focus();
        }
    }

    next() {
        if (this.group.length < 2) return;
        this.index = (this.index + 1) % this.group.length;
        this._render();
    }

    prev() {
        if (this.group.length < 2) return;
        this.index = (this.index - 1 + this.group.length) % this.group.length;
        this._render();
    }

    zoomIn() {
        const origin = this._getStageCenter();
        this._setZoom(Math.min(this.maxScale, this.scale + this.zoomStep), origin.x, origin.y);
    }

    zoomOut() {
        const origin = this._getStageCenter();
        this._setZoom(Math.max(this.minScale, this.scale - this.zoomStep), origin.x, origin.y);
    }

    resetZoom() {
        this._setZoom(1, 0, 0);
    }

    _collectGroup(trigger) {
        const groupName = trigger.dataset.lightboxGroup;
        if (!groupName) {
            this.group = [trigger];
            return;
        }
        this.group = Array.from(
            document.querySelectorAll(`[data-lightbox-group="${groupName}"]`)
        );
    }

    _render() {
        const trigger = this.group[this.index];
        if (!trigger) return;
        const src = trigger.dataset.lightboxSrc || '';
        const alt = trigger.dataset.lightboxAlt || '';
        const caption = trigger.dataset.lightboxCaption || '';

        if (!src) return;

        if (this.image.dataset.currentSrc !== src) {
            this.image.dataset.currentSrc = src;
            this.image.src = src;
        }
        this.image.alt = alt;

        this._resetZoomState();

        if (this.counterCurrent) this.counterCurrent.textContent = String(this.index + 1);
        if (this.counterTotal) this.counterTotal.textContent = String(this.group.length);

        const hasMultiple = this.group.length > 1;
        if (this.prevBtn) this.prevBtn.disabled = !hasMultiple;
        if (this.nextBtn) this.nextBtn.disabled = !hasMultiple;
    }

    _resetZoomState() {
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this._applyTransform();
        this._updateZoomLevel();
    }

    _setZoom(newScale, originX, originY) {
        const oldScale = this.scale;
        const scale = Math.max(this.minScale, Math.min(this.maxScale, newScale));

        if (scale === oldScale) {
            this._applyTransform();
            return;
        }

        const ratio = scale / oldScale;
        this.translateX = originX * (1 - ratio) + this.translateX * ratio;
        this.translateY = originY * (1 - ratio) + this.translateY * ratio;
        this.scale = scale;

        this._clampPan();
        this._applyTransform();
        this._updateZoomLevel();
    }

    _applyTransform() {
        if (!this.wrap) return;
        this.wrap.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
    }

    _updateZoomLevel() {
        if (!this.zoomLevel) return;
        this.zoomLevel.textContent = `${Math.round(this.scale * 100)}%`;
    }

    _clampPan() {
        if (!this.image || this.scale <= 1) {
            this.translateX = 0;
            this.translateY = 0;
            return;
        }
        const stageRect = this.stage.getBoundingClientRect();
        const imgRect = this.image.getBoundingClientRect();

        const overflowX = Math.max(0, (imgRect.width - stageRect.width) / 2);
        const overflowY = Math.max(0, (imgRect.height - stageRect.height) / 2);

        this.translateX = Math.max(-overflowX, Math.min(overflowX, this.translateX));
        this.translateY = Math.max(-overflowY, Math.min(overflowY, this.translateY));
    }

    _getStageCenter() {
        return { x: 0, y: 0 };
    }

    _getPointerInStage(e) {
        const rect = this.stage.getBoundingClientRect();
        const point = e.touches && e.touches.length
            ? e.touches[0]
            : (e.changedTouches && e.changedTouches.length ? e.changedTouches[0] : e);
        return {
            x: point.clientX - rect.left - rect.width / 2,
            y: point.clientY - rect.top - rect.height / 2,
        };
    }

    _getPinchOriginInStage(touches) {
        const rect = this.stage.getBoundingClientRect();
        const midX = (touches[0].clientX + touches[1].clientX) / 2;
        const midY = (touches[0].clientY + touches[1].clientY) / 2;
        return {
            x: midX - rect.left - rect.width / 2,
            y: midY - rect.top - rect.height / 2,
        };
    }

    _getPinchDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.hypot(dx, dy);
    }

    _onKeydown(e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            this.close();
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.next();
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.prev();
        } else if (e.key === '+' || e.key === '=') {
            e.preventDefault();
            this.zoomIn();
        } else if (e.key === '-' || e.key === '_') {
            e.preventDefault();
            this.zoomOut();
        } else if (e.key === '0') {
            e.preventDefault();
            this.resetZoom();
        }
    }

    _onWheel(e) {
        e.preventDefault();
        const origin = this._getPointerInStage(e);
        const delta = -e.deltaY * WHEEL_ZOOM_INTENSITY;
        this._setZoom(this.scale * (1 + delta), origin.x, origin.y);
    }

    _onMouseDown(e) {
        if (e.button !== 0) return;
        if (this.scale <= 1) return;
        e.preventDefault();
        this.isDragging = true;
        this.dragMoved = false;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;
        this.dragStartTx = this.translateX;
        this.dragStartTy = this.translateY;
        this.wrap.classList.add('is-dragging');
        this.stage.classList.add('is-panning');
    }

    _onMouseMove(e) {
        if (!this.isDragging) return;
        const dx = e.clientX - this.dragStartX;
        const dy = e.clientY - this.dragStartY;
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.dragMoved = true;
        this.translateX = this.dragStartTx + dx;
        this.translateY = this.dragStartTy + dy;
        this._clampPan();
        this._applyTransform();
    }

    _onMouseUp() {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.wrap.classList.remove('is-dragging');
        this.stage.classList.remove('is-panning');
    }

    _onDblClick(e) {
        e.preventDefault();
        const origin = this._getPointerInStage(e);
        if (this.scale > 1) {
            this._setZoom(1, origin.x, origin.y);
        } else {
            this._setZoom(2, origin.x, origin.y);
        }
    }

    _onTouchStart(e) {
        if (e.touches.length === 1) {
            const t = e.touches[0];
            const now = Date.now();
            const dx = t.clientX - this.lastTapX;
            const dy = t.clientY - this.lastTapY;
            const dist = Math.hypot(dx, dy);
            const isDoubleTap = (now - this.lastTapTime) < DOUBLE_TAP_THRESHOLD
                && dist < DOUBLE_TAP_DISTANCE;

            this.lastTapTime = now;
            this.lastTapX = t.clientX;
            this.lastTapY = t.clientY;

            if (isDoubleTap) {
                e.preventDefault();
                const origin = this._getPointerInStage(e);
                if (this.scale > 1) {
                    this._setZoom(1, origin.x, origin.y);
                } else {
                    this._setZoom(2, origin.x, origin.y);
                }
                this.touchState = null;
                return;
            }

            if (this.scale > 1) {
                e.preventDefault();
                this.touchState = {
                    type: 'pan',
                    startX: t.clientX,
                    startY: t.clientY,
                    startTx: this.translateX,
                    startTy: this.translateY,
                };
            }
        } else if (e.touches.length === 2) {
            e.preventDefault();
            this.touchState = {
                type: 'pinch',
                startDistance: this._getPinchDistance(e.touches),
                startScale: this.scale,
                origin: this._getPinchOriginInStage(e.touches),
            };
        }
    }

    _onTouchMove(e) {
        if (!this.touchState) return;
        e.preventDefault();

        if (this.touchState.type === 'pan' && e.touches.length === 1) {
            const t = e.touches[0];
            this.translateX = this.touchState.startTx + (t.clientX - this.touchState.startX);
            this.translateY = this.touchState.startTy + (t.clientY - this.touchState.startY);
            this._clampPan();
            this._applyTransform();
        } else if (this.touchState.type === 'pinch' && e.touches.length === 2) {
            const currentDistance = this._getPinchDistance(e.touches);
            const factor = currentDistance / this.touchState.startDistance;
            const newScale = this.touchState.startScale * factor;
            this._setZoom(newScale, this.touchState.origin.x, this.touchState.origin.y);
        }
    }

    _onTouchEnd(e) {
        if (!this.touchState) return;
        if (e.touches.length === 0) {
            this.touchState = null;
        } else if (e.touches.length === 1 && this.touchState.type === 'pinch') {
            const t = e.touches[0];
            this.touchState = {
                type: 'pan',
                startX: t.clientX,
                startY: t.clientY,
                startTx: this.translateX,
                startTy: this.translateY,
            };
        }
    }

    _attachStageEvents() {
        if (this._stageEventsBound) return;
        this._stageEventsBound = true;

        this._handlers.keydown = this._onKeydown.bind(this);
        this._handlers.wheel = this._onWheel.bind(this);
        this._handlers.mousedown = this._onMouseDown.bind(this);
        this._handlers.mousemove = this._onMouseMove.bind(this);
        this._handlers.mouseup = this._onMouseUp.bind(this);
        this._handlers.touchstart = this._onTouchStart.bind(this);
        this._handlers.touchmove = this._onTouchMove.bind(this);
        this._handlers.touchend = this._onTouchEnd.bind(this);
        this._handlers.dblclick = this._onDblClick.bind(this);
        this._handlers.resize = () => this._clampPan();

        document.addEventListener('keydown', this._handlers.keydown);
        this.stage.addEventListener('wheel', this._handlers.wheel, { passive: false });
        this.stage.addEventListener('mousedown', this._handlers.mousedown);
        document.addEventListener('mousemove', this._handlers.mousemove);
        document.addEventListener('mouseup', this._handlers.mouseup);
        this.stage.addEventListener('touchstart', this._handlers.touchstart, { passive: false });
        this.stage.addEventListener('touchmove', this._handlers.touchmove, { passive: false });
        this.stage.addEventListener('touchend', this._handlers.touchend);
        this.stage.addEventListener('dblclick', this._handlers.dblclick);
        window.addEventListener('resize', this._handlers.resize);
    }

    _detachStageEvents() {
        if (!this._stageEventsBound) return;
        this._stageEventsBound = false;

        document.removeEventListener('keydown', this._handlers.keydown);
        this.stage.removeEventListener('wheel', this._handlers.wheel);
        this.stage.removeEventListener('mousedown', this._handlers.mousedown);
        document.removeEventListener('mousemove', this._handlers.mousemove);
        document.removeEventListener('mouseup', this._handlers.mouseup);
        this.stage.removeEventListener('touchstart', this._handlers.touchstart);
        this.stage.removeEventListener('touchmove', this._handlers.touchmove);
        this.stage.removeEventListener('touchend', this._handlers.touchend);
        this.stage.removeEventListener('dblclick', this._handlers.dblclick);
        window.removeEventListener('resize', this._handlers.resize);
    }

    _saveScrollbarWidth() {
        const width = window.innerWidth - document.documentElement.clientWidth;
        if (width > 0) {
            document.documentElement.style.setProperty('--scrollbar-width', `${width}px`);
        }
    }

    _build() {
        const el = document.createElement('div');
        el.className = 'lightbox';
        el.setAttribute('role', 'dialog');
        el.setAttribute('aria-modal', 'true');
        el.setAttribute('aria-label', 'Bildansicht');
        el.innerHTML = `
            <div class="lightbox__backdrop" data-lightbox-close aria-hidden="true"></div>
            <div class="lightbox__header">
                <span class="lightbox__counter">
                    <span data-lightbox-current>1</span>
                    <span aria-hidden="true"> / </span>
                    <span data-lightbox-total>1</span>
                </span>
                <button type="button" class="lightbox__close" data-lightbox-close aria-label="Schließen">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>
            <button type="button" class="lightbox__nav lightbox__nav--prev" data-lightbox-prev aria-label="Vorheriges Bild">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
            </button>
            <div class="lightbox__stage is-idle" data-lightbox-stage>
                <div class="lightbox__image-wrap" data-lightbox-wrap>
                    <img class="lightbox__image" data-lightbox-image alt="" draggable="false">
                </div>
            </div>
            <button type="button" class="lightbox__nav lightbox__nav--next" data-lightbox-next aria-label="Nächstes Bild">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
            </button>
            <div class="lightbox__toolbar" role="toolbar" aria-label="Zoom-Werkzeuge">
                <button type="button" class="lightbox__tool" data-lightbox-zoom-out aria-label="Verkleinern">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="7"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                </button>
                <span class="lightbox__zoom-level" data-lightbox-zoom-level aria-live="polite">100%</span>
                <button type="button" class="lightbox__tool" data-lightbox-zoom-in aria-label="Vergrößern">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="7"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        <line x1="11" y1="8" x2="11" y2="14"></line>
                        <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                </button>
                <button type="button" class="lightbox__tool" data-lightbox-zoom-reset aria-label="Zoom zurücksetzen">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <polyline points="9 14 4 9 9 4"></polyline>
                        <path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>
                    </svg>
                </button>
            </div>
        `;
        document.body.appendChild(el);

        this.el = el;
        this.stage = el.querySelector('[data-lightbox-stage]');
        this.wrap = el.querySelector('[data-lightbox-wrap]');
        this.image = el.querySelector('[data-lightbox-image]');
        this.zoomLevel = el.querySelector('[data-lightbox-zoom-level]');
        this.counterCurrent = el.querySelector('[data-lightbox-current]');
        this.counterTotal = el.querySelector('[data-lightbox-total]');
        this.prevBtn = el.querySelector('[data-lightbox-prev]');
        this.nextBtn = el.querySelector('[data-lightbox-next]');

        const closeBtn = el.querySelector('.lightbox__close');
        this.closeBtn = closeBtn;

        el.addEventListener('click', (e) => {
            if (e.target.closest('[data-lightbox-close]')) {
                e.stopPropagation();
                this.close();
            }
        });

        el.querySelector('[data-lightbox-prev]').addEventListener('click', (e) => {
            e.stopPropagation();
            this.prev();
        });
        el.querySelector('[data-lightbox-next]').addEventListener('click', (e) => {
            e.stopPropagation();
            this.next();
        });
        el.querySelector('[data-lightbox-zoom-in]').addEventListener('click', (e) => {
            e.stopPropagation();
            this.zoomIn();
        });
        el.querySelector('[data-lightbox-zoom-out]').addEventListener('click', (e) => {
            e.stopPropagation();
            this.zoomOut();
        });
        el.querySelector('[data-lightbox-zoom-reset]').addEventListener('click', (e) => {
            e.stopPropagation();
            this.resetZoom();
        });

        this.image.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.scale > 1) {
                this.resetZoom();
            } else {
                const origin = this._getPointerInStage(e);
                this._setZoom(2, origin.x, origin.y);
            }
        });
    }
}

let instance = null;

export function initLightbox() {
    if (!instance) {
        instance = new Lightbox();
    }
    return instance;
}
